/**
 * useCachedMessages — IndexedDB message caching via idb-keyval
 *
 * Adapted from bachata-nation-next useCachedMessages.ts.
 * Provides offline-first message access with local caching.
 */

import { useState, useEffect, useCallback } from 'react';
import { get, set } from 'idb-keyval';
import type { ChatMessage } from '../types';

// Cache freshness: 5 minutes
const CACHE_TTL_MS = 5 * 60 * 1000;

function cacheKey(conversationId: string): string {
  return `chat:messages:${conversationId}`;
}

function metaKey(conversationId: string): string {
  return `chat:meta:${conversationId}`;
}

interface CacheMeta {
  lastSyncAt: number;
  messageCount: number;
}

interface UseCachedMessagesOptions {
  conversationId: string;
  enabled?: boolean;
}

interface UseCachedMessagesReturn {
  cachedMessages: ChatMessage[];
  isFresh: boolean;
  stats: { messageCount: number; lastSyncAt: number | null };
  cacheServerMessages: (messages: ChatMessage[]) => Promise<void>;
}

export function useCachedMessages({
  conversationId,
  enabled = true,
}: UseCachedMessagesOptions): UseCachedMessagesReturn {
  const [cachedMessages, setCachedMessages] = useState<ChatMessage[]>([]);
  const [isFresh, setIsFresh] = useState(false);
  const [stats, setStats] = useState<{ messageCount: number; lastSyncAt: number | null }>({
    messageCount: 0,
    lastSyncAt: null,
  });

  // Load cached messages on mount
  useEffect(() => {
    if (!enabled || !conversationId) return;

    const loadCache = async () => {
      try {
        const [messages, meta] = await Promise.all([
          get<ChatMessage[]>(cacheKey(conversationId)),
          get<CacheMeta>(metaKey(conversationId)),
        ]);

        if (messages) {
          setCachedMessages(messages);
        }

        const fresh = meta ? Date.now() - meta.lastSyncAt < CACHE_TTL_MS : false;
        setIsFresh(fresh);
        setStats({
          messageCount: meta?.messageCount || 0,
          lastSyncAt: meta?.lastSyncAt || null,
        });
      } catch (error) {
        console.warn('[useCachedMessages] Failed to load cache:', error);
      }
    };

    loadCache();
  }, [conversationId, enabled]);

  // Cache messages from server response
  const cacheServerMessages = useCallback(
    async (messages: ChatMessage[]) => {
      if (!conversationId || messages.length === 0) return;

      try {
        // Merge with existing cache (avoid duplicates)
        const existing = (await get<ChatMessage[]>(cacheKey(conversationId))) || [];
        const existingIds = new Set(existing.map(m => m.id));
        const newMessages = messages.filter(m => !existingIds.has(m.id));
        const merged = [...existing, ...newMessages].sort(
          (a, b) => a.sequenceId - b.sequenceId
        );

        // Keep only last 500 messages per conversation
        const trimmed = merged.slice(-500);

        await set(cacheKey(conversationId), trimmed);
        await set(metaKey(conversationId), {
          lastSyncAt: Date.now(),
          messageCount: trimmed.length,
        } satisfies CacheMeta);

        setStats({
          messageCount: trimmed.length,
          lastSyncAt: Date.now(),
        });
        setIsFresh(true);
      } catch (error) {
        console.warn('[useCachedMessages] Failed to cache messages:', error);
      }
    },
    [conversationId]
  );

  return {
    cachedMessages,
    isFresh,
    stats,
    cacheServerMessages,
  };
}

export default useCachedMessages;
